import React from 'react';
import Autosuggest from 'react-autosuggest';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import Cross from '@components/Cross';
import { SearchLoader } from '@components/Loading';

import './style.scss';

const GIS_SEARCH_TITLE = 'GIS Validated Addresses';
const GIS_NA_SUGGESTION_NAME = 'GIS NA. Please enter manually.';
const GIS_NA_SUGGESTION_VALUE = 'GIS_NA';

const COMCATE_SEARCH_TITLE = 'Comcate Application Addresses';
const COMCATE_NA_SUGGESTION_NAME = 'App data NA. Please enter manually';
const COMCATE_NA_SUGGESTION_VALUE = 'COMCATE_NA';

const MAP_ADDRESS_SEARCH_TITLE = 'Map Addresses';
const MAP_ADDRESS_NA_SUGGESTION_NAME = 'Map Address NA. Please enter manually.';
const MAP_ADDRESS_NA_SUGGESTION_VALUE = 'MAP_ADDRESS_NA';

declare type TSearch = (query: string) => void;
declare type TSuggestionSelect = (
  query: string,
  value: any,
  locationDetails: SearchAddressDetails,
  selectedSection?: string
) => void;

interface IDisplaySearchItem {
  name: string;
  value: any;
  isDefault: boolean;
  locationDetails?: SearchAddressDetails;
}

interface IDisplaySearchOptions {
  title?: string;
  items: IDisplaySearchItem[];
}

interface ISearchboxProps {
  query: string;
  multiSection: boolean;
  searchResults: ISearchOptions[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  defaultFunction?: {
    label: string;
    onClick: any;
    display?: boolean;
    displayPosition?: string | 'top' | 'bottom';
    icon?: string;
  };
  shouldHighlightMatchingSubstring?: boolean;
  defaultSuggestions?: ISearchOptions[];
  emptyMessage?: string | JSX.Element;
  onQueryChange: (newValue: string) => void;
  updateQueryOnArrowKeys: boolean;
  onSuggestionSelect: TSuggestionSelect;
  clearSuggestions: () => void;
  onFocus?: (e?: any) => void;
  onBlur?: () => void;
  search: TSearch;
  setDefaultFunctionDisplay?: (status: boolean) => void;
  onSuggestionsClearRequested?: () => void;
  alwaysRenderSuggestions?: boolean;
  renderSuggestion?: (suggestionElement: JSX.Element, suggestionValue: any, suggestionObject?: any) => JSX.Element;
  renderSuggestionSectionTitle?: boolean;
  onLoadMoreData?: () => void;
  showCustomInput?: boolean;
  cancelSearch?: () => void;
  // from redux
  matchQueryInResultAfter?: number;
  isDefaultFunctionCalled: boolean;
  inputRef?: React.RefObject<any>;
  allowSuggestionSelectBubbling: boolean;
  focusInputOnSuggestionClick: boolean;
  isPinDropSearch?: boolean;
  showFixedOptionInMultiSection?: boolean;
  showError?: boolean;
  lessenPadding?: boolean;
  'aria-label'?: string;
}

interface ISearchboxState {
  suggestions: IDisplaySearchOptions[];
  suggestionSelectedRecently: boolean;
}

export default class Searchbox extends React.Component<ISearchboxProps, ISearchboxState> {
  static defaultProps: ISearchboxProps = {
    query: '',
    onQueryChange: () => null,
    multiSection: false,
    clearSuggestions: () => null,
    onFocus: () => null,
    onSuggestionSelect: () => null,
    setDefaultFunctionDisplay: (status: boolean) => null,
    searchResults: [],
    emptyMessage: null,
    placeholder: 'Search',
    search: null,
    disabled: false,
    defaultFunction: null,
    defaultSuggestions: null,
    shouldHighlightMatchingSubstring: true,
    onSuggestionsClearRequested: null,
    alwaysRenderSuggestions: false,
    renderSuggestion: null,
    renderSuggestionSectionTitle: true,
    matchQueryInResultAfter: 0,
    isDefaultFunctionCalled: false,
    updateQueryOnArrowKeys: true,
    allowSuggestionSelectBubbling: true,
    focusInputOnSuggestionClick: false,
    isPinDropSearch: false,
    showFixedOptionInMultiSection: false,
  };

  private debouncedScrollHandler;

  constructor(props: ISearchboxProps) {
    super(props);
    this.debouncedScrollHandler = debounce((element: HTMLElement) => this.handleContainerOnScroll(element), 100);
    this.state = {
      suggestions: [],
      suggestionSelectedRecently: false,
    };
  }

  componentDidUpdate(prevProps: ISearchboxProps, prevState: ISearchboxState) {
    const { isDefaultFunctionCalled, searchResults, query } = this.props;
    const newState = {} as ISearchboxState;

    if (
      (prevState.suggestionSelectedRecently && prevProps.query.trim() !== '') ||
      (!prevProps.isDefaultFunctionCalled && prevState.suggestionSelectedRecently)
    ) {
      newState.suggestionSelectedRecently = false;
      newState.suggestions = [];
    }

    if (isDefaultFunctionCalled && !prevProps.isDefaultFunctionCalled) {
      this.setState(
        (prevState) => ({
          ...prevState,
          suggestions: [],
          suggestionSelectedRecently: true,
        }),
        () => {
          prevProps.clearSuggestions();
          this.setDefaultSuggestions('');
        }
      );
    } else if (!isEqual(searchResults, prevProps.searchResults) || query !== prevProps.query) {
      if (query.trim() || prevProps.isPinDropSearch) {
        const suggestions = searchResults ? this.getDisplaySuggestions(searchResults) : [];

        newState.suggestions = suggestions;
      } else if (!isEqual(searchResults, prevProps.searchResults)) {
        this.setDefaultSuggestions('');
      }
    }

    if (Object.keys(newState).length) {
      this.setState(newState);
    }
  }

  getDisplaySuggestions = (suggestions: ISearchOptions[]): IDisplaySearchOptions[] =>
    suggestions.map((searchItem) => {
      if (searchItem.title === GIS_SEARCH_TITLE && searchItem.items.length === 0) {
        return {
          title: searchItem.title,
          items: [{ name: GIS_NA_SUGGESTION_NAME, value: GIS_NA_SUGGESTION_VALUE, isDefault: false }],
        };
      }

      if (searchItem.title === COMCATE_SEARCH_TITLE && searchItem.items.length === 0) {
        return {
          title: searchItem.title,
          items: [{ name: COMCATE_NA_SUGGESTION_NAME, value: COMCATE_NA_SUGGESTION_VALUE, isDefault: false }],
        };
      }

      if (searchItem.title === MAP_ADDRESS_SEARCH_TITLE && searchItem.items.length === 0) {
        return {
          title: searchItem.title,
          items: [{ name: MAP_ADDRESS_NA_SUGGESTION_NAME, value: MAP_ADDRESS_NA_SUGGESTION_VALUE, isDefault: false }],
        };
      }

      return {
        title: searchItem.title,
        items: searchItem.items.map((suggestion) => ({
          ...suggestion,
          isDefault: false,
        })),
      };
    });

  setDefaultSuggestions = (currentQuery: string) => {
    if (currentQuery.length === 0 && this.props.defaultSuggestions && !this.state.suggestionSelectedRecently) {
      this.setState({
        suggestions: this.props.defaultSuggestions.map((searchItem) => ({
          title: searchItem.title,
          items: searchItem.items.map((suggestion) => ({
            ...suggestion,
            isDefault: true,
          })),
        })),
      });
    }
  };

  loadSuggestions = (value: string) => {
    if (this.props.defaultSuggestions && value.length === 0) {
      this.setDefaultSuggestions(value);
    } else {
      this.props.search(value);
    }
  };

  onChange = (event, { newValue }) => {
    if (event.type !== 'click') {
      if (this.props.updateQueryOnArrowKeys) {
        this.props.onQueryChange(newValue);
      } else {
        const arrowKeys = ['ArrowDown', 'ArrowUp'];

        if (!arrowKeys.includes(event.key)) {
          this.props.onQueryChange(newValue);
        }
      }
    }
  };

  onSuggestionsFetchRequested = ({ value, reason }) => {
    if (reason !== 'suggestion-selected') {
      this.loadSuggestions(value);
    }
  };

  onSuggestionsClearRequested = () => {
    const suggestions = [];
    this.setState(
      (prevState) => ({
        ...prevState,
        suggestions,
      }),
      () => {
        if (this.props.onSuggestionsClearRequested) {
          this.props.onSuggestionsClearRequested();
        }
      }
    );
  };

  onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) => {
    if (!this.props.allowSuggestionSelectBubbling) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }

    const sectionSelected = this.props.searchResults[sectionIndex] && this.props.searchResults[sectionIndex].title;

    if (suggestion.value === GIS_NA_SUGGESTION_VALUE || suggestion.value === COMCATE_NA_SUGGESTION_VALUE
      || suggestion.value === MAP_ADDRESS_NA_SUGGESTION_VALUE
    ) {
      this.defaultFunctionClick(event);

      return;
    }
    this.setState(
      (prevState) => ({
        ...prevState,
        suggestions: [],
        suggestionSelectedRecently: true,
      }),
      () => {
        this.props.onSuggestionSelect(suggestion.name, suggestion.value, suggestion.locationDetails, sectionSelected);
        this.props.clearSuggestions();
      }
    );
  };

  getSuggestionValue = (suggestion) => {
    if (suggestion.value === GIS_NA_SUGGESTION_VALUE || suggestion.value === COMCATE_NA_SUGGESTION_VALUE
      || suggestion.value === MAP_ADDRESS_NA_SUGGESTION_VALUE
    ) {
      return '';
    } else {
      return suggestion.name;
    }
  };

  getSectionSuggestions = (section: ISearchOptions) => (section ? section.items : null);

  isMultiSection = () =>
    this.props.multiSection || (this.props.query.length === 0 && this.props.defaultSuggestions !== null);

  showEmptyMessage = (): boolean => this.props.emptyMessage && this.props.query && this.props.query.length > 0;

  handleContainerOnScroll = (element: HTMLElement) => {
    // check if top scroll reached bottom
    if (element && element.scrollHeight - element.scrollTop <= element.clientHeight + 40) {
      this.props.onLoadMoreData();
    }
  };

  defaultFunctionClick = (e) => {
    this.props.defaultFunction.onClick(e);
    this.setState({ suggestions: [] });
  };
  displayAvailable = (defaultFunction) => defaultFunction && defaultFunction.display;
  renderSuggestionsContainer = (props) => {
    const { containerProps, children } = props;
    const { defaultFunction, emptyMessage, onLoadMoreData } = this.props;
    const renderDefaultFunction = (position: 'top' | 'bottom') => {
      if (!this.displayAvailable(defaultFunction) || defaultFunction.displayPosition !== position) {
        return null;
      }

      return (
        <div
          className={`react-autosuggest__function${this.props.showFixedOptionInMultiSection ? '__sticky' : ''}`}
          role="option"
          aria-selected="false"
          tabIndex={0}
          onClick={this.defaultFunctionClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.defaultFunctionClick(event);
            }
          }}
        >
          <i className={classnames(`react-autosuggest__${defaultFunction.icon}-icon`)} />
          <span>{defaultFunction.label}</span>
        </div>
      );
    };

    return (
      <div {...containerProps} onScroll={(e) => (onLoadMoreData ? this.debouncedScrollHandler(e.target) : null)}>
        {renderDefaultFunction('top')}
        {children ? (
          <>
            <div className={classnames('react-autosuggest-sections')}>{children}</div>
          </>
        ) : (
          this.showEmptyMessage() && (
            <div className={classnames(`react-autosuggest__empty-message${this.props.lessenPadding ? '1' : ''}`)}>
              {emptyMessage}
            </div>
          )
        )}
        {renderDefaultFunction('bottom')}
      </div>
    );
  };

  renderSuggestion = (suggestion) => {
    const { shouldHighlightMatchingSubstring, query } = this.props;
    const renderSuggestionFromProps = this.props.renderSuggestion;
    let suggestionLabel = suggestion.name;

    if (shouldHighlightMatchingSubstring && suggestionLabel) {
      const escape = (str: string) => str.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
      const queryRegex = new RegExp(escape(query), 'ig');
      suggestionLabel = suggestionLabel.replace(queryRegex, '<b>$&</b>');
    }

    const suggestionElement = (
      <div className={classnames('react-autosuggest__suggestion-element')}>
        {suggestion.isDefault && <div className={classnames('history-icon')} />}
        <div className={classnames('list-label')} dangerouslySetInnerHTML={{ __html: suggestionLabel }} />
      </div>
    );

    return renderSuggestionFromProps
      ? renderSuggestionFromProps(suggestionElement, suggestion.value, suggestion)
      : suggestionElement;
  };

  renderSectionTitle = (section: ISearchOptions) => {
    const isDefaultSuggestionRendered = this.props.query.length === 0;

    if (isDefaultSuggestionRendered) {
      // if the default option list title
      // should be displayed.
      return <label className="react-autosuggest__section-title--default">{section.title}</label>;
    } else if (this.props.renderSuggestionSectionTitle && this.props.multiSection) {
      // display multiSection title.
      return <label className="react-autosuggest__section-title--default">{section.title}</label>;
    } else {
      // display nothing if not
      // multiSection.
      return null;
    }
  };

  onKeyboardInputFocus = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (this.props.setDefaultFunctionDisplay) {
      this.props.setDefaultFunctionDisplay(true);
    }
    this.setDefaultSuggestions(this.props.query);
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  renderInputComponent = (inputProps) => {
    const { loading, query, cancelSearch } = this.props;

    return (
      <div className="input-container">
        <input {...inputProps} />
        {query?.trim() && (loading ? <SearchLoader size={40} /> : <Cross size={20} onClick={cancelSearch} />)}
      </div>
    );
  };

  render() {
    const inputProps = {
      placeholder: this.props.placeholder,
      value: this.props.query,
      onChange: this.onChange,
      disabled: this.props.disabled,
      onFocus: this.onKeyboardInputFocus,
      onBlur: this.props.onBlur,
      autoComplete: 'new-password',
      'aria-label': this.props['aria-label'],
      'data-lpignore': 'true',
      'data-1p-ignore': true,
      'data-form-type': 'other',
    };
    let className = 'search-box';
    if (this.props.showError) {
      className += ' error-style';
    }
    return (
      <div className={className}>
        <Autosuggest
          multiSection={true}
          suggestions={this.state.suggestions}
          alwaysRenderSuggestions={this.props.alwaysRenderSuggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionSelected={this.onSuggestionSelected}
          renderSuggestionsContainer={this.renderSuggestionsContainer}
          renderSuggestion={this.renderSuggestion}
          renderSectionTitle={this.renderSectionTitle}
          focusInputOnSuggestionClick={this.props.focusInputOnSuggestionClick}
          getSectionSuggestions={this.getSectionSuggestions}
          getSuggestionValue={this.getSuggestionValue}
          inputProps={inputProps}
          ref={this.props.inputRef}
          {...(this.props.showCustomInput && { renderInputComponent: this.renderInputComponent })}
        />
      </div>
    );
  }
}
